import { defineComponent as _defineComponent } from 'vue';
import { toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, createTextVNode as _createTextVNode, createElementVNode as _createElementVNode, vModelText as _vModelText, withDirectives as _withDirectives, withModifiers as _withModifiers, normalizeStyle as _normalizeStyle, resolveComponent as _resolveComponent, createVNode as _createVNode, renderList as _renderList, Fragment as _Fragment, vModelDynamic as _vModelDynamic, renderSlot as _renderSlot, Transition as _Transition, withCtx as _withCtx } from "vue";
const _hoisted_1 = { class: "admin-table-section" };
const _hoisted_2 = {
    key: 0,
    class: "pin-icon"
};
const _hoisted_3 = {
    key: 2,
    class: "filter-badge"
};
const _hoisted_4 = ["placeholder"];
const _hoisted_5 = {
    key: 0,
    class: "table-wrapper"
};
const _hoisted_6 = {
    key: 0,
    class: "list-loading"
};
const _hoisted_7 = {
    key: 1,
    class: "table-content"
};
const _hoisted_8 = ["onClick", "title"];
const _hoisted_9 = {
    key: 0,
    class: "sort-indicator"
};
const _hoisted_10 = ["onMousedown"];
const _hoisted_11 = {
    key: 0,
    class: "header-cell"
};
const _hoisted_12 = { key: 0 };
const _hoisted_13 = ["onClick"];
const _hoisted_14 = ["data-label"];
const _hoisted_15 = ["onUpdate:modelValue", "placeholder", "disabled"];
const _hoisted_16 = ["onUpdate:modelValue", "type", "placeholder", "disabled"];
const _hoisted_17 = { class: "cell-value" };
const _hoisted_18 = {
    key: 0,
    class: "col-actions"
};
const _hoisted_19 = {
    key: 0,
    class: "action-btn-spinner"
};
const _hoisted_20 = ["disabled"];
const _hoisted_21 = ["onClick", "disabled"];
const _hoisted_22 = ["onClick"];
const _hoisted_23 = {
    key: 2,
    class: "action-btn-spinner"
};
const _hoisted_24 = {
    key: 1,
    class: "table-empty"
};
const _hoisted_25 = ["data-label"];
const _hoisted_26 = ["onUpdate:modelValue", "placeholder", "readonly", "disabled"];
const _hoisted_27 = ["onUpdate:modelValue", "type", "placeholder", "disabled"];
const _hoisted_28 = {
    key: 3,
    class: "cell-value"
};
const _hoisted_29 = {
    key: 0,
    class: "col-actions"
};
const _hoisted_30 = {
    key: 0,
    class: "action-btn-spinner"
};
const _hoisted_31 = ["disabled"];
import { ref, reactive, computed, watch, onMounted, useSlots, getCurrentInstance } from 'vue';
export default /*@__PURE__*/ _defineComponent({
    __name: 'AdminTable',
    props: {
        title: {},
        icon: { default: '' },
        columns: {},
        data: { default: () => [] },
        loading: { type: Boolean, default: false },
        searchable: { type: Boolean, default: false },
        searchPlaceholder: { default: 'Search...' },
        showClearButton: { type: Boolean, default: false },
        collapsible: { type: Boolean, default: false },
        defaultExpanded: { type: Boolean, default: true },
        editable: { type: Boolean, default: false },
        selectable: { type: Boolean, default: false },
        deletable: { type: Boolean, default: false },
        addable: { type: Boolean, default: false },
        emptyMessage: { default: 'No data available' },
        badge: { default: null },
        externalSearchQuery: { default: '' },
        savingRowId: { default: null },
        addingRow: { type: Boolean, default: false },
        deletingRowId: { default: null }
    },
    emits: ["row-select", "row-edit-start", "row-edit-save", "row-edit-cancel", "row-delete", "row-add", "row-action", "search-update", "search-clear"],
    setup(__props, { expose: __expose, emit: __emit }) {
        const slots = useSlots();
        const instance = getCurrentInstance();
        const props = __props;
        const emit = __emit;
        const isExpanded = ref(props.defaultExpanded);
        const internalSearchQuery = ref('');
        const sortByField = ref(null);
        const sortOrderState = ref('asc');
        const editingRowId = ref(null);
        const editingData = reactive({});
        const selectedRowId = ref(null);
        const addRowData = reactive({});
        const addRowInitial = reactive({});
        const localColumns = ref([]);
        const resizingColumnIndex = ref(null);
        const startX = ref(0);
        let startWidth = 0;
        // Create a deep copy of the columns to make them mutable
        localColumns.value = JSON.parse(JSON.stringify(props.columns));
        const gridTemplateColumns = computed(() => {
            const columnWidths = localColumns.value.map((col) => col.width || '1fr').join(' ');
            // Fixed (not auto) so the Actions track is identical in every row — otherwise rows with
            // different action content (trash icon vs empty add-row vs save/cancel) get different track
            // widths, which rebalances the 1fr columns and knocks them out of alignment between rows.
            return hasActions.value ? `${columnWidths} 100px` : columnWidths;
        });
        const searchQuery = computed(() => {
            return props.externalSearchQuery || internalSearchQuery.value;
        });
        const filteredSortedData = computed(() => {
            let result = [...props.data];
            // Apply search filter
            if (searchQuery.value.trim()) {
                const query = searchQuery.value.toLowerCase();
                result = result.filter(row => {
                    return props.columns.some(col => {
                        const value = row[col.field];
                        if (value === null || value === undefined)
                            return false;
                        return String(value).toLowerCase().includes(query);
                    });
                });
            }
            // Apply sorting
            if (sortByField.value) {
                result.sort((a, b) => {
                    const column = props.columns.find(col => col.field === sortByField.value);
                    let aVal = a[sortByField.value];
                    let bVal = b[sortByField.value];
                    // Handle date type
                    if (column?.type === 'date') {
                        aVal = new Date(aVal || 0).getTime();
                        bVal = new Date(bVal || 0).getTime();
                    }
                    else {
                        // Handle text/number
                        aVal = (aVal || '').toString().toLowerCase();
                        bVal = (bVal || '').toString().toLowerCase();
                    }
                    if (aVal < bVal)
                        return sortOrderState.value === 'asc' ? -1 : 1;
                    if (aVal > bVal)
                        return sortOrderState.value === 'asc' ? 1 : -1;
                    return 0;
                });
            }
            return result;
        });
        const isAddRowDirty = computed(() => {
            return Object.keys(addRowData).some(key => addRowData[key] !== addRowInitial[key]);
        });
        const isAddRowValid = computed(() => {
            return props.columns
                .filter(col => col.required)
                .every(col => addRowData[col.field]);
        });
        const hasActions = computed(() => {
            return props.editable || props.deletable || props.addable || !!slots['custom-actions'];
        });
        watch(internalSearchQuery, (val) => {
            emit('search-update', val);
        });
        function startResize(event, columnIndex) {
            resizingColumnIndex.value = columnIndex;
            startX.value = event.clientX;
            startWidth = instance?.proxy?.$el.querySelectorAll('.header-cell')[columnIndex].offsetWidth ?? 0;
            document.addEventListener('mousemove', doResize);
            document.addEventListener('mouseup', stopResize);
        }
        function doResize(event) {
            if (resizingColumnIndex.value === null)
                return;
            const dx = event.clientX - startX.value;
            const newWidth = startWidth + dx;
            // Ensure newWidth is not smaller than a minimum width (e.g., 50px)
            if (newWidth > 50) {
                localColumns.value[resizingColumnIndex.value].width = `${newWidth}px`;
            }
        }
        function stopResize() {
            resizingColumnIndex.value = null;
            document.removeEventListener('mousemove', doResize);
            document.removeEventListener('mouseup', stopResize);
        }
        function toggleExpanded() {
            isExpanded.value = !isExpanded.value;
        }
        function clearSearch() {
            internalSearchQuery.value = '';
            emit('search-clear');
        }
        function handleSort(field) {
            if (sortByField.value === field) {
                sortOrderState.value = sortOrderState.value === 'asc' ? 'desc' : 'asc';
            }
            else {
                sortByField.value = field;
                sortOrderState.value = 'asc';
            }
        }
        function handleRowClick(row) {
            if (!editingRowId.value && props.selectable) {
                selectedRowId.value = selectedRowId.value === row.id ? null : row.id;
                emit('row-select', row);
            }
        }
        function startEdit(row) {
            editingRowId.value = row.id;
            Object.keys(editingData).forEach(key => delete editingData[key]);
            props.columns.forEach(col => {
                editingData[col.field] = row[col.field] || '';
            });
            emit('row-edit-start', row);
        }
        function saveEdit() {
            emit('row-edit-save', {
                rowId: editingRowId.value,
                data: { ...editingData }
            });
            // Parent is responsible for calling cancelEdit() on success
        }
        function cancelEdit() {
            editingRowId.value = null;
            Object.keys(editingData).forEach(key => delete editingData[key]);
            emit('row-edit-cancel');
        }
        function handleDelete(row) {
            emit('row-delete', row);
        }
        function initAddRow() {
            Object.keys(addRowData).forEach(key => delete addRowData[key]);
            Object.keys(addRowInitial).forEach(key => delete addRowInitial[key]);
            props.columns.forEach(col => {
                addRowData[col.field] = col.default ?? '';
                addRowInitial[col.field] = col.default ?? '';
            });
        }
        function submitAddRow() {
            emit('row-add', { ...addRowData });
            // Parent is responsible for calling resetAddRow() on success
        }
        function resetAddRow() {
            initAddRow();
        }
        function getInputType(type) {
            const typeMap = {
                'email': 'email',
                'url': 'url',
                'number': 'number',
                'date': 'date',
                'textarea': 'textarea',
                'color': 'color'
            };
            return typeMap[type] || 'text';
        }
        function formatCellValue(value, type) {
            if (value === null || value === undefined || value === '')
                return '—';
            switch (type) {
                case 'date':
                    if (typeof value === 'string') {
                        const date = new Date(value);
                        return date.toLocaleDateString('en-GB', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric'
                        });
                    }
                    return value;
                case 'number':
                    return Number(value).toLocaleString();
                default:
                    return value;
            }
        }
        __expose({ cancelEdit, resetAddRow });
        onMounted(() => {
            initAddRow();
        });
        return (_ctx, _cache) => {
            const _component_v_skeleton_loader = _resolveComponent("v-skeleton-loader");
            const _component_v_progress_circular = _resolveComponent("v-progress-circular");
            return (_openBlock(), _createElementBlock("div", _hoisted_1, [
                _createElementVNode("div", {
                    class: "section-header",
                    onClick: _cache[2] || (_cache[2] = ($event) => (_ctx.collapsible && toggleExpanded())),
                    style: _normalizeStyle({ cursor: _ctx.collapsible ? 'pointer' : 'default' })
                }, [
                    _createElementVNode("h2", null, [
                        (_ctx.icon)
                            ? (_openBlock(), _createElementBlock("span", _hoisted_2, _toDisplayString(_ctx.icon), 1))
                            : _createCommentVNode("", true),
                        _createTextVNode(" " + _toDisplayString(_ctx.title) + " ", 1),
                        (_ctx.collapsible)
                            ? (_openBlock(), _createElementBlock("span", {
                                key: 1,
                                class: _normalizeClass(["expand-icon", { rotated: isExpanded.value }])
                            }, "›", 2))
                            : _createCommentVNode("", true),
                        (_ctx.badge)
                            ? (_openBlock(), _createElementBlock("span", _hoisted_3, _toDisplayString(_ctx.badge), 1))
                            : _createCommentVNode("", true)
                    ]),
                    (_ctx.searchable)
                        ? (_openBlock(), _createElementBlock("div", {
                            key: 0,
                            class: "search-controls",
                            onClick: _cache[1] || (_cache[1] = _withModifiers(() => { }, ["stop"]))
                        }, [
                            _withDirectives(_createElementVNode("input", {
                                "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event) => ((internalSearchQuery).value = $event)),
                                type: "text",
                                placeholder: _ctx.searchPlaceholder,
                                class: "search-input"
                            }, null, 8, _hoisted_4), [
                                [_vModelText, internalSearchQuery.value]
                            ]),
                            (_ctx.showClearButton || searchQuery.value)
                                ? (_openBlock(), _createElementBlock("button", {
                                    key: 0,
                                    class: "reset-filter-btn",
                                    onClick: clearSearch,
                                    title: "Clear filter"
                                }, " Clear Filter ✕ "))
                                : _createCommentVNode("", true)
                        ]))
                        : _createCommentVNode("", true)
                ], 4),
                _createVNode(_Transition, { name: "expand" }, {
                    default: _withCtx(() => [
                        (!_ctx.collapsible || isExpanded.value)
                            ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
                                (_ctx.loading)
                                    ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
                                        _createVNode(_component_v_skeleton_loader, {
                                            type: "table",
                                            loading: _ctx.loading
                                        }, null, 8, ["loading"])
                                    ]))
                                    : (_openBlock(), _createElementBlock("div", _hoisted_7, [
                                        _createElementVNode("div", {
                                            class: "table-header",
                                            style: _normalizeStyle({ gridTemplateColumns: gridTemplateColumns.value })
                                        }, [
                                            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(localColumns.value, (column, index) => {
                                                return (_openBlock(), _createElementBlock("div", {
                                                    key: column.field,
                                                    class: _normalizeClass(['header-cell', { sortable: column.sortable }]),
                                                    onClick: ($event) => (column.sortable && handleSort(column.field)),
                                                    title: column.sortable ? 'Click to sort' : ''
                                                }, [
                                                    _createTextVNode(_toDisplayString(column.label) + " ", 1),
                                                    (column.sortable && sortByField.value === column.field)
                                                        ? (_openBlock(), _createElementBlock("span", _hoisted_9, _toDisplayString(sortOrderState.value === 'asc' ? ' ↑' : ' ↓'), 1))
                                                        : _createCommentVNode("", true),
                                                    _createElementVNode("div", {
                                                        class: "resizer",
                                                        onMousedown: _withModifiers(($event) => (startResize($event, index)), ["stop"])
                                                    }, null, 40, _hoisted_10)
                                                ], 10, _hoisted_8));
                                            }), 128)),
                                            (hasActions.value)
                                                ? (_openBlock(), _createElementBlock("div", _hoisted_11, "Actions"))
                                                : _createCommentVNode("", true)
                                        ], 4),
                                        (filteredSortedData.value.length > 0)
                                            ? (_openBlock(), _createElementBlock("div", _hoisted_12, [
                                                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(filteredSortedData.value, (row, index) => {
                                                    return (_openBlock(), _createElementBlock("div", {
                                                        key: row.id || index,
                                                        class: _normalizeClass(["table-row", {
                                                                'selected': _ctx.selectable && selectedRowId.value === row.id,
                                                                'editing': _ctx.editable && editingRowId.value === row.id
                                                            }]),
                                                        onClick: ($event) => (handleRowClick(row)),
                                                        style: _normalizeStyle({
                                                            gridTemplateColumns: gridTemplateColumns.value,
                                                            cursor: (_ctx.selectable || _ctx.editable) ? 'pointer' : 'default'
                                                        })
                                                    }, [
                                                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(localColumns.value, (column) => {
                                                            return (_openBlock(), _createElementBlock("div", {
                                                                key: column.field,
                                                                class: _normalizeClass(`col-${column.field}`),
                                                                "data-label": column.label
                                                            }, [
                                                                (_ctx.editable && editingRowId.value === row.id && column.editable)
                                                                    ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                                                                        (column.type === 'textarea')
                                                                            ? _withDirectives((_openBlock(), _createElementBlock("textarea", {
                                                                                key: 0,
                                                                                "onUpdate:modelValue": ($event) => ((editingData[column.field]) = $event),
                                                                                placeholder: column.placeholder,
                                                                                class: "cell-input",
                                                                                disabled: _ctx.savingRowId === row.id,
                                                                                onClick: _cache[3] || (_cache[3] = _withModifiers(() => { }, ["stop"]))
                                                                            }, null, 8, _hoisted_15)), [
                                                                                [_vModelText, editingData[column.field]]
                                                                            ])
                                                                            : _withDirectives((_openBlock(), _createElementBlock("input", {
                                                                                key: 1,
                                                                                "onUpdate:modelValue": ($event) => ((editingData[column.field]) = $event),
                                                                                type: getInputType(column.type),
                                                                                placeholder: column.placeholder,
                                                                                class: "cell-input",
                                                                                disabled: _ctx.savingRowId === row.id,
                                                                                onClick: _cache[4] || (_cache[4] = _withModifiers(() => { }, ["stop"]))
                                                                            }, null, 8, _hoisted_16)), [
                                                                                [_vModelDynamic, editingData[column.field]]
                                                                            ])
                                                                    ], 64))
                                                                    : _renderSlot(_ctx.$slots, `cell-${column.field}`, {
                                                                        key: 1,
                                                                        row: row,
                                                                        value: row[column.field],
                                                                        editingData: editingData,
                                                                        editingRowId: editingRowId.value
                                                                    }, () => [
                                                                        _createElementVNode("span", _hoisted_17, _toDisplayString(formatCellValue(row[column.field], column.type)), 1)
                                                                    ])
                                                            ], 10, _hoisted_14));
                                                        }), 128)),
                                                        (hasActions.value)
                                                            ? (_openBlock(), _createElementBlock("div", _hoisted_18, [
                                                                (_ctx.editable && editingRowId.value === row.id)
                                                                    ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                                                                        (_ctx.savingRowId === row.id)
                                                                            ? (_openBlock(), _createElementBlock("div", _hoisted_19, [
                                                                                _createVNode(_component_v_progress_circular, {
                                                                                    indeterminate: "",
                                                                                    size: "20",
                                                                                    width: "2"
                                                                                })
                                                                            ]))
                                                                            : (_openBlock(), _createElementBlock("button", {
                                                                                key: 1,
                                                                                class: "action-btn save",
                                                                                onClick: _withModifiers(saveEdit, ["stop"]),
                                                                                title: "Save"
                                                                            }, "💾")),
                                                                        _createElementVNode("button", {
                                                                            class: "action-btn cancel",
                                                                            onClick: _withModifiers(cancelEdit, ["stop"]),
                                                                            title: "Cancel",
                                                                            disabled: _ctx.savingRowId === row.id
                                                                        }, "✕", 8, _hoisted_20)
                                                                    ], 64))
                                                                    : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                                                                        (_ctx.editable)
                                                                            ? (_openBlock(), _createElementBlock("button", {
                                                                                key: 0,
                                                                                class: "action-btn edit",
                                                                                onClick: _withModifiers(($event) => (startEdit(row)), ["stop"]),
                                                                                title: "Edit",
                                                                                disabled: _ctx.deletingRowId === row.id
                                                                            }, "✏️", 8, _hoisted_21))
                                                                            : _createCommentVNode("", true),
                                                                        _renderSlot(_ctx.$slots, "custom-actions", { row: row }),
                                                                        (_ctx.deletable && _ctx.deletingRowId !== row.id)
                                                                            ? (_openBlock(), _createElementBlock("button", {
                                                                                key: 1,
                                                                                class: "action-btn delete",
                                                                                onClick: _withModifiers(($event) => (handleDelete(row)), ["stop"]),
                                                                                title: "Delete"
                                                                            }, "🗑️", 8, _hoisted_22))
                                                                            : _createCommentVNode("", true),
                                                                        (_ctx.deletingRowId === row.id)
                                                                            ? (_openBlock(), _createElementBlock("div", _hoisted_23, [
                                                                                _createVNode(_component_v_progress_circular, {
                                                                                    indeterminate: "",
                                                                                    size: "20",
                                                                                    width: "2"
                                                                                })
                                                                            ]))
                                                                            : _createCommentVNode("", true)
                                                                    ], 64))
                                                            ]))
                                                            : _createCommentVNode("", true)
                                                    ], 14, _hoisted_13));
                                                }), 128))
                                            ]))
                                            : (_openBlock(), _createElementBlock("div", _hoisted_24, [
                                                _createElementVNode("p", null, _toDisplayString(_ctx.emptyMessage), 1)
                                            ])),
                                        (_ctx.addable)
                                            ? (_openBlock(), _createElementBlock("div", {
                                                key: 2,
                                                class: "table-row add-row",
                                                style: _normalizeStyle({ gridTemplateColumns: gridTemplateColumns.value })
                                            }, [
                                                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(localColumns.value, (column) => {
                                                    return (_openBlock(), _createElementBlock("div", {
                                                        key: `add-${column.field}`,
                                                        class: _normalizeClass(`col-${column.field}`),
                                                        "data-label": column.label
                                                    }, [
                                                        (column.type === 'textarea')
                                                            ? _withDirectives((_openBlock(), _createElementBlock("textarea", {
                                                                key: 0,
                                                                "onUpdate:modelValue": ($event) => ((addRowData[column.field]) = $event),
                                                                placeholder: column.placeholder,
                                                                class: "cell-input",
                                                                readonly: !column.editable,
                                                                disabled: _ctx.addingRow
                                                            }, null, 8, _hoisted_26)), [
                                                                [_vModelText, addRowData[column.field]]
                                                            ])
                                                            : (column.editable)
                                                                ? _withDirectives((_openBlock(), _createElementBlock("input", {
                                                                    key: 1,
                                                                    "onUpdate:modelValue": ($event) => ((addRowData[column.field]) = $event),
                                                                    type: getInputType(column.type),
                                                                    placeholder: column.placeholder,
                                                                    class: "cell-input",
                                                                    disabled: _ctx.addingRow
                                                                }, null, 8, _hoisted_27)), [
                                                                    [_vModelDynamic, addRowData[column.field]]
                                                                ])
                                                                : (_ctx.$slots[`add-cell-${column.field}`])
                                                                    ? _renderSlot(_ctx.$slots, `add-cell-${column.field}`, {
                                                                        key: 2,
                                                                        addRowData: addRowData
                                                                    })
                                                                    : (_openBlock(), _createElementBlock("span", _hoisted_28, _toDisplayString(column.addRowDefault || '—'), 1))
                                                    ], 10, _hoisted_25));
                                                }), 128)),
                                                (hasActions.value)
                                                    ? (_openBlock(), _createElementBlock("div", _hoisted_29, [
                                                        (isAddRowDirty.value)
                                                            ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                                                                (_ctx.addingRow)
                                                                    ? (_openBlock(), _createElementBlock("div", _hoisted_30, [
                                                                        _createVNode(_component_v_progress_circular, {
                                                                            indeterminate: "",
                                                                            size: "20",
                                                                            width: "2"
                                                                        })
                                                                    ]))
                                                                    : (isAddRowValid.value)
                                                                        ? (_openBlock(), _createElementBlock("button", {
                                                                            key: 1,
                                                                            class: "action-btn save",
                                                                            onClick: submitAddRow,
                                                                            title: "Save"
                                                                        }, "💾"))
                                                                        : _createCommentVNode("", true),
                                                                _createElementVNode("button", {
                                                                    class: "action-btn cancel",
                                                                    onClick: resetAddRow,
                                                                    title: "Cancel",
                                                                    disabled: _ctx.addingRow
                                                                }, "✕", 8, _hoisted_31)
                                                            ], 64))
                                                            : _createCommentVNode("", true)
                                                    ]))
                                                    : _createCommentVNode("", true)
                                            ], 4))
                                            : _createCommentVNode("", true)
                                    ]))
                            ]))
                            : _createCommentVNode("", true)
                    ]),
                    _: 3
                })
            ]));
        };
    }
});
